import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./ongoing-tab.module.scss";
import { useLazyGetOngoingServiceRequestsQuery } from "../../../../../service/customersupport-service";
import { ServiceRequestInstance } from "../../../../../model/service-request/service-request-instance";
import ServiceRequestListItem from "../../components/service-request-list-item/ServiceRequestListItem";
import { ApplicationRoutes } from "../../../../../routes";
import { formatCurrency, formatDateWithOrdinal } from "../../../../../utils";
import { getStatusText } from "../../service-request.utils";

const PAGE_SIZE = 10;
const LOADING_COUNT = 3;

const OngoingTab: React.FC = () => {
	const navigate = useNavigate();
	const [fetchOngoing, { isError }] = useLazyGetOngoingServiceRequestsQuery();

	const [items, setItems] = useState<ServiceRequestInstance[]>([]);
	const [hasMore, setHasMore] = useState(true);
	const [initialized, setInitialized] = useState(false);
	const [isFetchingMore, setIsFetchingMore] = useState(false);

	const pageRef = useRef(1);
	const loadingRef = useRef(false);
	const hasMoreRef = useRef(true);
	const observerRef = useRef<IntersectionObserver | null>(null);
	const initializedRef = useRef(false);

	const loadNextPage = useCallback(async () => {
		if (loadingRef.current || !hasMoreRef.current) return;
		loadingRef.current = true;
		setIsFetchingMore(true);
		const result = await fetchOngoing({ page: pageRef.current, pageSize: PAGE_SIZE });
		if (result.data) {
			setItems(prev => [...prev, ...(result.data!.items as ServiceRequestInstance[])]);
			hasMoreRef.current = result.data.hasNextPage;
			setHasMore(result.data.hasNextPage);
			pageRef.current += 1;
		}
		loadingRef.current = false;
		setIsFetchingMore(false);
		if (!initializedRef.current) {
			initializedRef.current = true;
			setInitialized(true);
		}
	}, [fetchOngoing]);

	useEffect(() => {
		loadNextPage();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const sentinelCallback = useCallback(
		(node: HTMLDivElement | null) => {
			if (observerRef.current) observerRef.current.disconnect();
			if (!node) return;
			observerRef.current = new IntersectionObserver(
				(entries) => {
					if (entries[0].isIntersecting) loadNextPage();
				},
				{ rootMargin: "100px", threshold: 0.1 }
			);
			observerRef.current.observe(node);
		},
		[loadNextPage]
	);

	const handleItemTap = (id: number) => {
		navigate(`/${ApplicationRoutes.CUSTOMER_SERVICE_REQUEST_DETAIL.replace(":id", String(id))}`);
	};

	if (!initialized) {
		return (
			<div className={styles.container}>
				<div className={styles.card}>
					{Array.from({ length: LOADING_COUNT }).map((_, i) => (
						<React.Fragment key={i}>
							<div className={styles.skeletonRow} />
							{i < LOADING_COUNT - 1 && <div className={styles.divider} />}
						</React.Fragment>
					))}
				</div>
			</div>
		);
	}

	if (isError && items.length === 0) {
		return (
			<div className={styles.emptyContainer}>
				<span className={styles.emptyText}>Failed to load ongoing requests</span>
			</div>
		);
	}

	if (items.length === 0) {
		return (
			<div className={styles.emptyContainer}>
				<span className={styles.emptyText}>No ongoing service requests</span>
			</div>
		);
	}

	return (
		<div className={styles.container}>
			<div className={styles.card}>
				{items.map((item, i) => (
					<React.Fragment key={item.id}>
						<ServiceRequestListItem
							title={item.serviceRequest?.name ?? ""}
							date={formatDateWithOrdinal(item.createdAt, item.updatedAt)}
							amount={item.price?.amount ? formatCurrency(item.price.amount, { currency: item.price.currency }) : ""}
							percentage={0}
							status={getStatusText(item.status)}
							onTap={() => handleItemTap(item.id)}
						/>
						{i < items.length - 1 && <div className={styles.divider} />}
					</React.Fragment>
				))}
				{hasMore && (
				<div ref={sentinelCallback} className={styles.sentinel}>
					{isFetchingMore && <div className={styles.bottomLoader} />}
				</div>
			)}
			</div>

			
		</div>
	);
};

export default OngoingTab;
